// =====================================================================
//  TRADUCCIÓN DE LOS ERRORES DEL MOTOR
//
//  Las reglas del Código de Minería viven en triggers de Postgres, y cada
//  una lanza su propio SQLSTATE. Acá se convierten en mensajes que citan
//  el artículo, en vez de mostrarle al analista un volcado de Postgres.
//
//  Los códigos los define supabase/migrations/20260729120300_funciones.sql
//  y se eligieron para no tener que parsear el texto del mensaje.
// =====================================================================

/**
 * Error de permiso que el motor NO reporta como error.
 *
 * Detalle de Postgres que es fácil de pasar por alto: un UPDATE o un
 * DELETE bloqueado por la cláusula USING de una política RLS no lanza
 * excepción — simplemente afecta cero filas. Solo los INSERT, que se
 * validan con WITH CHECK, devuelven error.
 *
 * Sin esta comprobación una escritura denegada se vería como exitosa y la
 * interfaz mostraría un cambio que la base de datos nunca guardó.
 *
 * Vive acá, y no junto al cliente, porque es un tipo de error y porque
 * leerError() lo distingue con `instanceof`: si hubiera dos definiciones
 * de esta clase, esa comprobación fallaría en silencio.
 */
export class SinPermiso extends Error {
  constructor(public detalle = 'La operación no afectó ninguna fila') {
    super(
      'No tienes permiso para este cambio, o la fila ya no existe. ' +
        'Si tu rol es Lector, la clasificación la hacen los analistas.',
    )
    this.name = 'SinPermiso'
  }
}

export interface Regla {
  titulo: string
  articulo: string
  ayuda: string
}

export const REGLAS: Record<string, Regla> = {
  MN149: {
    titulo: 'La postura está bajo el mínimo de subasta',
    articulo: 'art. 149 C. de Minería',
    ayuda:
      'El mínimo de la subasta es el valor de las patentes adeudadas, no una tasación. ' +
      'Una postura menor no sería admisible. Dejala en 0 si todavía no la definiste.',
  },
  MN249: {
    titulo: 'Falta acreditar el pago del doble',
    articulo: 'art. 149 inc. 2 C. de Minería',
    ayuda:
      'El titular puede sacar la concesión del remate hasta el momento mismo del remate, ' +
      'pagando el doble de lo adeudado. Para marcarla RETIRADA hay que cargar la fecha de ' +
      'pago y un monto de al menos el doble, y la fecha no puede ser posterior al remate.',
  },
  MN349: {
    titulo: 'La adjudicación no puede ser bajo el mínimo',
    articulo: 'art. 149 C. de Minería',
    ayuda:
      'Para registrar una adjudicación hay que indicar el monto, y no puede ser inferior al ' +
      'mínimo de subasta.',
  },
  MN449: {
    titulo: 'Terreno franco es, por definición, el lote sin posturas',
    articulo: 'sin postores — art. 147 y siguientes',
    ayuda:
      'Esta concesión tiene monto de adjudicación registrado. Si de verdad quedó sin ' +
      'postores, borra primero el monto y el adjudicatario.',
  },
  MN147: {
    titulo: 'La publicación no cumple los 30 días de antelación',
    articulo: 'art. 147 C. de Minería',
    ayuda:
      'La publicación en el Diario Oficial debe anteceder al remate en al menos 30 días ' +
      'corridos, sin contar el día de publicación ni el del remate. Para el remate del 21 de ' +
      'octubre, el último día posible es el 20 de septiembre.',
  },
  MN500: {
    titulo: 'Esta concesión figura con $0 adeudados',
    articulo: 'art. 149 C. de Minería',
    ayuda:
      'Con deuda cero el mínimo de subasta sería cero y no hay aritmética de remate posible. ' +
      'Solicita certificado de deuda a la TGR antes de marcarla de interés: o ya pagó y la nómina ' +
      'no se actualizó, o hay un error de la Tesorería.',
  },
  MN600: {
    titulo: 'La bitácora es inmutable',
    articulo: '',
    ayuda: 'La bitácora registra, no se corrige. Ni el máster puede editarla o borrarla.',
  },
  MN700: {
    titulo: 'El correo maestro está protegido',
    articulo: '',
    ayuda:
      'consorciosalinero@gmail.com no puede cambiar de rol, desactivarse, renombrarse ni ' +
      'eliminarse, ni siquiera desde la base de datos.',
  },
}

/** Códigos genéricos de Postgres que conviene traducir igual. */
const GENERICOS: Record<string, string> = {
  '23505': 'Ese valor ya existe: hay otro registro con la misma clave.',
  '23503': 'Hay registros que dependen de este y no se puede borrar mientras existan.',
  '23514': 'El valor está fuera del rango que permite el esquema.',
  '22P02': 'El valor no corresponde a ninguna de las opciones válidas.',
  '42501': 'Tu rol no tiene permiso para esta operación.',
}

export interface ErrorLegible {
  titulo: string
  detalle: string
  articulo?: string
  /** true si es una de las cuatro reglas del Código de Minería. */
  esRegla: boolean
}

export function leerError(e: unknown): ErrorLegible {
  if (e instanceof SinPermiso) {
    return { titulo: 'Cambio no aplicado', detalle: e.message, esRegla: false }
  }

  const err = e as { code?: string; message?: string } | null
  const codigo = err?.code

  if (codigo && REGLAS[codigo]) {
    const r = REGLAS[codigo]
    return {
      titulo: r.titulo,
      detalle: r.ayuda,
      articulo: r.articulo || undefined,
      esRegla: true,
    }
  }

  if (codigo && GENERICOS[codigo]) {
    return { titulo: 'No se pudo guardar', detalle: GENERICOS[codigo], esRegla: false }
  }

  const mensaje = err?.message ?? String(e)
  if (/failed to fetch|networkerror/i.test(mensaje)) {
    return {
      titulo: 'Sin conexión',
      detalle: 'No se pudo llegar al servidor. El cambio no se guardó; vuelve a intentarlo.',
      esRegla: false,
    }
  }

  return { titulo: 'No se pudo guardar', detalle: mensaje, esRegla: false }
}
